import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import {
    CurrentLocationInfoWindow,
    LocationGoogleMapMarkerType,
    LocationGoogleMapsMarker,
    LocationInfoWindow,
} from "@insite/client-framework/Common/Hooks/useLocationGoogleMarkers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { newGuid } from "@insite/client-framework/Common/StringHelpers";
import withDynamicGoogleMaps from "@insite/client-framework/Common/withDynamicGoogleMaps";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoCondensedDisplay, {
    AddressInfoCondensedDisplayStyles,
} from "@insite/content-library/Components/AddressInfoCondensedDisplay";
import DistanceDisplay, { DistanceUnitOfMeasure } from "@insite/content-library/Components/DistanceDisplay";
import GoogleMapsDirectionLink from "@insite/content-library/Components/GoogleMapsDirectionLink";
import LocationContentLink from "@insite/content-library/Components/LocationContentLink";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { GoogleMapProps, InfoWindowProps, MarkerProps } from "@react-google-maps/api";
import * as React from "react";
import { css } from "styled-components";
import { getMarkerIcon } from "@NA/Utils/Utility";

interface Props {
    extendedStyles?: LocationGoogleMapStyles;
    currentLocation?: google.maps.LatLng;
    locationSearchFilter: string;
    distanceUnitOfMeasure: DistanceUnitOfMeasure;
    mapMarkerElements: LocationGoogleMapsMarker[];
    locationInfoWindow?: LocationInfoWindow<LocationModel>;
    currentLocationInfoWindow?: CurrentLocationInfoWindow;
    handleOpenLocationContent: (location: LocationModel) => void;
    setGoogleMap: (map: google.maps.Map | undefined) => void;
    GoogleMap: React.ComponentType<GoogleMapProps>;
    InfoWindow: React.ComponentType<InfoWindowProps>;
    Marker: React.ComponentType<MarkerProps>;
    locationsPagination: any;
    hoveredIndex: number | null;
    onMarkerHover: (index: number | null) => void;
    selectedLocationIndex: string;
}

export interface LocationGoogleMapStyles {
    googleMap?: GoogleMapProps;
    locationMarker?: MarkerProps;
    currentLocationMarker?: MarkerProps;
    selectedMarker?: MarkerProps;
    locationInfoWindow?: InfoWindowProps;
    currentLocationInfoWindow?: InfoWindowProps;
    infoWindowGridContainer?: GridContainerProps;
    infoWindowLeftGridItem?: GridItemProps;
    selectedLocationName?: TypographyPresentationProps;
    selectedLocationAddressDisplay?: AddressInfoCondensedDisplayStyles;
    infoWindowRightGridItem?: GridItemProps;
    infoWindowLocationContentLink?: LinkPresentationProps;
    infoWindowGoogleMapsDirectionLink?: LinkPresentationProps;
    infoWindowDistanceDisplay?: TypographyPresentationProps;
    currentLocationInfoWindowGridContainer?: GridContainerProps;
    currentLocationInfoWindowHeaderGridItem?: GridItemProps;
    currentLocationInfoWindowHeaderText?: TypographyPresentationProps;
    currentLocationInfoWindowSearchFilterGridItem?: GridItemProps;
    currentLocationInfoWindowSearchFilterText?: TypographyPresentationProps;
}
import styled from "styled-components";

const MapContainer = styled.div`
    height: 690px; /* Default height */
    width: 100%;

    @media (max-width: 76px) {
        height: 500px;  !important/* Height for tablets */
    }
`;
export const locationGoogleMapStyles: LocationGoogleMapStyles = {
    googleMap: {
        zoom: 14,
        mapContainerStyle: {
            // height: "680px",
            width: "100%",
            height: typeof window === 'undefined' ? "680px" : window.innerWidth <= 767 ? "200px" : window.innerWidth <= 1024 ? "436px" : "680px",
        },

    },
    infoWindowGridContainer: {
        gap: 0,
    },
    infoWindowLeftGridItem: {
        width: 8,
        css: css`
            flex-direction: column;
        `,
    },
    infoWindowRightGridItem: {
        width: 4,
        css: css`
            flex-direction: column;
        `,
    },
    selectedLocationName: {
        color: "primary",
        weight: "bold",
    },
    currentLocationInfoWindowGridContainer: {
        gap: 0,
    },
    currentLocationInfoWindowHeaderGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    currentLocationInfoWindowSearchFilterGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
};

const LocationGoogleMap: React.FC<Props> = ({
    extendedStyles,
    currentLocation,
    locationSearchFilter,
    distanceUnitOfMeasure,
    setGoogleMap,
    mapMarkerElements,
    locationInfoWindow,
    currentLocationInfoWindow,
    handleOpenLocationContent,
    GoogleMap,
    InfoWindow,
    Marker,
    locationsPagination,
    hoveredIndex,
    onMarkerHover,
    selectedLocationIndex,
}) => {
    const [styles] = React.useState(() => mergeToNew(locationGoogleMapStyles, extendedStyles));
    const [firstValue, setFirstValue] = React.useState("");

    //const startIndex = (locationsPagination.currentPage - 1) * locationsPagination.pageSize + 1;
    const startIndex = (locationsPagination?.currentPage - 1) * locationsPagination?.pageSize + 1 || 1;

    const handleLoad = (map: google.maps.Map) => {
        setGoogleMap(map);
    };
    const handleUnmount = (_: google.maps.Map) => {
        setGoogleMap(undefined);
    };

    const getMarkerStyles = (markerType: LocationGoogleMapMarkerType) => {
        if (markerType === "CURRENT_LOCATION") {
            return styles.currentLocationMarker;
        }
        if (markerType === "SELECTED") {
            return styles.selectedMarker;
        }
        return styles.locationMarker;
    };
    let url: any;
    if (typeof window !== "undefined") {
        url = window.location.href.includes("DealerDetails");
    }
    // const getMarkerIcon = (index: any, isHovered: boolean, marker: any): string => {
    //     const storedValue = localStorage.getItem("storedValue");

    //     const backgroundColor = url ? "#fff" : (isHovered ? "#fff" : "#2d5787");
    //     const textColor = url ? "#2d5787" : (isHovered ? "#2d5787" : "#fff");
    //     const borderColor = url ? "#2d5787" : "#2d5787";  
    //     return `data:image/svg+xml;base64,${btoa(`
    //         <svg xmlns="http://www.w3.org/2000/svg" width="40" height="60">
    //             <!-- Rectangle with background color -->
    //             <rect width="31" height="35" x="4.5" y="1" style="fill:${backgroundColor}; stroke:${borderColor}; stroke-width:1;" />

    //             <!-- Display either stored value or index -->
    //             <text x="20" y="23" font-size="15" fill="${textColor}" text-anchor="middle" font-family="Arial">${
    //                 url ? storedValue : index
    //             }</text>

    //             <!-- Downward-pointing triangle (aligned with the center of the rectangle's bottom edge) -->
    //             <path d="M15.5 36 L24.5 36 L20 42 Z" fill="${backgroundColor}" /> <!-- Arrow pointing down with tip at bottom -->

    //             <!-- Left border for the triangle -->
    //             <path d="M15.5 36 L20 42" style="fill:none; stroke:${borderColor}; stroke-width:1;" /> <!-- Left border -->

    //             <!-- Right border for the triangle -->
    //             <path d="M24.5 36 L20 42" style="fill:none; stroke:${borderColor}; stroke-width:1;" /> <!-- Right border -->
    //         </svg>
    //     `)}`;


    // };

    React.useEffect(() => {
        if (selectedLocationIndex !== null && selectedLocationIndex !== undefined && selectedLocationIndex !== "") {
            setFirstValue(selectedLocationIndex);

            localStorage.setItem("storedValue", selectedLocationIndex);
        }
    }, [selectedLocationIndex]);
    React.useEffect(() => {
        if (selectedLocationIndex !== null && selectedLocationIndex !== undefined && selectedLocationIndex !== "") {
        }
    }, [firstValue, locationsPagination]);

    return (
        <GoogleMap {...styles.googleMap} center={currentLocation} onLoad={handleLoad} onUnmount={handleUnmount} >
            {mapMarkerElements.map((marker, index) => {
                let adjustedIndex;
                if (
                    selectedLocationIndex !== null &&
                    selectedLocationIndex !== undefined &&
                    selectedLocationIndex !== ""
                ) {
                    adjustedIndex = selectedLocationIndex;

                    const storedValue = localStorage.getItem("storedValue");
                } else {
                    adjustedIndex = startIndex + index;
                }

                if (marker.key !== "current-location") {
                    return (
                        <Marker
                            key={marker.key}
                            position={marker.position}
                            icon={getMarkerIcon(adjustedIndex, index === hoveredIndex, marker)}
                            onMouseOver={() => !url && onMarkerHover(index)}
                            onMouseOut={() => !url && onMarkerHover(null)}
                        />
                    );
                }
            })}

            {locationInfoWindow && (
                <InfoWindow {...styles.locationInfoWindow} key={newGuid()} position={locationInfoWindow?.position}>
                    {locationInfoWindow && (
                        <GridContainer {...styles.infoWindowGridContainer}>
                            <GridItem {...styles.infoWindowLeftGridItem}>
                                <Typography {...styles.selectedLocationName}>
                                    {locationInfoWindow.location.name}
                                </Typography>
                                <AddressInfoCondensedDisplay
                                    {...locationInfoWindow.location}
                                    extendedStyles={styles.selectedLocationAddressDisplay}
                                />
                            </GridItem>
                            <GridItem {...styles.infoWindowRightGridItem}>
                                {locationInfoWindow.location.htmlContent && (
                                    <LocationContentLink
                                        {...styles.infoWindowLocationContentLink}
                                        location={locationInfoWindow.location}
                                        onOpenLocationContent={handleOpenLocationContent}
                                    />
                                )}
                                <GoogleMapsDirectionLink
                                    {...styles.infoWindowGoogleMapsDirectionLink}
                                    {...locationInfoWindow.location}
                                />
                                {locationInfoWindow.location.distance > 0.01 && (
                                    <DistanceDisplay
                                        {...styles.infoWindowDistanceDisplay}
                                        distance={locationInfoWindow.location.distance}
                                        unitOfMeasure={distanceUnitOfMeasure}
                                    />
                                )}
                            </GridItem>
                        </GridContainer>
                    )}
                </InfoWindow>
            )}
            {currentLocationInfoWindow && (
                <InfoWindow
                    {...styles.currentLocationInfoWindow}
                    key={newGuid()}
                    position={currentLocationInfoWindow.position}
                >
                    <GridContainer {...styles.currentLocationInfoWindowGridContainer}>
                        <GridItem {...styles.currentLocationInfoWindowHeaderGridItem}>
                            <Typography {...styles.currentLocationInfoWindowHeaderText}>
                                {translate("Current Location")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.currentLocationInfoWindowSearchFilterGridItem}>
                            <Typography {...styles.currentLocationInfoWindowSearchFilterText}>
                                {locationSearchFilter}
                            </Typography>
                        </GridItem>
                    </GridContainer>
                </InfoWindow>
            )}
        </GoogleMap>
    );
};

export default withDynamicGoogleMaps(LocationGoogleMap);
